import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from "vite-react-ssg";
import NavigationButtons from "@/components/NavigationButtons";
import RelatedLinks from "@/components/RelatedLinks";

const canonical = "https://deepseek.ai/blog/leak-catl-deepseek-investor";
const title = "CATL Invests 5bn Yuan in DeepSeek: Round Closed 15 July 2026";
const description =
  "Battery giant CATL took a 5 billion yuan (~$740M) stake in DeepSeek's 50 billion yuan round, closed 15 July 2026. What the passive stake does and does not mean.";

const html = `
<p><em>Disclaimer: deepseek.ai is an independent community and fan site, not officially affiliated with DeepSeek AI. Figures below follow reporting by Bloomberg, CnEVPost, TechNode and Pandaily.</em></p>

<p>What began as a May 2026 report has been confirmed: <a href="https://www.catl.com/en/" target="_blank" rel="noopener noreferrer">CATL</a> — the world's largest battery manufacturer — is an investor in DeepSeek. The financing <strong>closed on 15 July 2026</strong>, and CATL's contribution is <strong>5 billion yuan (roughly $740 million)</strong>.</p>

<h2>One Round, Two Numbers</h2>
<p>The "50 billion yuan round" and the "$7.4 billion round" are the <strong>same financing</strong>, quoted in different currencies — not two separate raises. Total: ~50 billion yuan ≈ $7.4 billion, valuing DeepSeek near $50 billion. Our two deep-dives cover the same event from different angles: the <a href="/blog/deepseek-50-billion-funding-round">structure of the 50 billion yuan round</a> and the <a href="/blog/deepseek-raises-7-4-billion-bizarre-deal">founder-centric deal geometry</a>.</p>

<h2>CATL's Position in the Cap Table</h2>
<ul>
  <li><strong>Amount:</strong> 5 billion yuan (~$740 million).</li>
  <li><strong>Share of the round:</strong> roughly <strong>10%</strong> of the ~50 billion yuan raised — the second-largest external cheque after Tencent.</li>
  <li><strong>Rights:</strong> reporting describes external investors in this round as holding economic interest without voting rights, under a multi-year lock-up. Founder Liang Wenfeng retains control with a reported ~39% stake.</li>
</ul>

<h2>What This Is — and What It Isn't</h2>
<p>It is a <strong>passive financial stake</strong>. Neither CATL nor DeepSeek has announced an operational partnership: no joint datacenter energy-storage programme, no supply agreement, no shared infrastructure roadmap. Earlier speculation on this site framed the investment as an energy-buffering alliance; that inference was not supported by any primary source and has been removed.</p>
<p>The reasonable read is simpler. Chinese industrial champions with large balance sheets are allocating capital to the country's leading open-weight AI lab. Strategic collaboration may follow — but as of 25 July 2026 nothing of the sort has been disclosed.</p>

<h2>Bottom Line</h2>
<p>CATL is now on DeepSeek's cap table for 5 billion yuan, as part of a closed ~50 billion yuan round. Treat it as capital, not as a technology alliance, until either company says otherwise.</p>
`;


const schemaData = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: title,
  datePublished: "2026-05-22T10:45:23+00:00",
  dateModified: "2026-07-25T00:00:00+00:00",
  author: { "@type": "Organization", name: "The Independent DeepSeek Guide", url: "https://deepseek.ai" },
  publisher: {
    "@type": "Organization",
    name: "The Independent DeepSeek Guide",
    logo: { "@type": "ImageObject", url: "https://deepseek.ai/logo.png" },
  },
  description,
  mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
};

const LeakCatlDeepseekInvestor = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Head>
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate("/blog")} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Blog
        </Button>
        <h1 className="text-4xl md:text-5xl font-bold mb-6">
          CATL Invests 5bn Yuan in DeepSeek: Round Closed 15 July 2026
        </h1>
        <p className="text-muted-foreground mb-8">Published 22 May 2026 · Updated 25 July 2026 with the closed round figures</p>
        <article
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <NavigationButtons />
        <RelatedLinks />
      </main>
    </div>
  );
};

export default LeakCatlDeepseekInvestor;
